<script setup lang="ts">
import type { CmsMedia } from '~/composables/useCmsMedia';
import type { CurrentQuestion, QuizAnswer, QuizAnswerState } from '~/types/quiz.types';
import type { QuestionAnswersType } from "~/enums/quiz.enums";

const { t } = useI18n();

const props = withDefaults(defineProps<{
  questionText: string;
  type: QuestionAnswersType.Multiple | QuestionAnswersType.Single;
  answers: QuizAnswerState[];
  feedbackVideo: CmsMedia | undefined;
  isQuestionCorrect?: boolean | null;
  showUserAnswer?: boolean;
}>(), {
  showUserAnswer: true,
  isQuestionCorrect: null,
});

const modelValue = defineModel<boolean>();

const currentQuestion = computed<CurrentQuestion>((): CurrentQuestion => {
  return {
    isCorrect: props.isQuestionCorrect,
    isRevealingAnswer: true,
    selectedAnswers: props.answers.filter(answer => answer.isSelected).map(answer => {
      return {
        id: answer.id,
        answer: answer.answer,
      };
    }) as QuizAnswer[],
    correctAnswers: props.answers.filter(answer => answer.isCorrect).map(answer => answer.id) ?? [],
    question: {
      type: props.type,
      question: props.questionText,
      answers: props.answers.map(answer => {
        return {
          id: answer.id,
          answer: answer.answer,
        };
      }) as QuizAnswer[],
    },
  };
});
</script>

<template>
  <Dialog
    v-model:visible="modelValue"
    :draggable="false"
    modal
    :header="t('course.quiz.question.expertAnswer.title')"
    class="sm:w-[1200px]"
    :pt="{
      content: {
        class: 'p-0'
      }
    }"
  >
    <template #default>
      <div class="flex lg:flex-row flex-col">
        <div class="flex items-center w-full aspect-video bg-black">
          <LessonPlayer
            v-if="feedbackVideo"
            class="!rounded-none"
            :vimeo-id="feedbackVideo?.source!"
          />
        </div>

        <div class="w-full lg:max-w-[400px] bg-neutral-20 p-4">
          <div
            v-sanitize-html="questionText"
            class="text-neutral-100 text-20 font-bold pb-4"
          />

          <div class="flex flex-col gap-4">
            <QuizAnswers
              readonly
              hide-title
              :current-question="currentQuestion as CurrentQuestion"
            />
          </div>

          <!-- fixme: should be inside QuizAnswers component -->
          <div
            v-if="showUserAnswer && isQuestionCorrect !== null && typeof isQuestionCorrect !== 'undefined'"
            class="text-14 font-bold text-center text-white rounded-lg mt-6 py-1 px-4 uppecrase"
            :class="isQuestionCorrect ? 'bg-green-60' : 'bg-red-50'"
          >
            {{isQuestionCorrect ? t('course.quiz.question.answer.correct') : t('course.quiz.question.answer.incorrect')}}
          </div>
        </div>
      </div>
    </template>
  </Dialog>
</template>
